import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PageHeader } from '@/shared/ui/PageHeader'
import { FilterBar } from '@/shared/ui/FilterBar'
import { DataTable } from '@/shared/ui/DataTable'
import { StatusBadge } from '@/shared/ui/Badge'
import { Card } from '@/shared/ui/Card'
import { StatCard } from '@/shared/ui/StatCard'
import { Modal } from '@/shared/ui/Modal'
import { Button } from '@/shared/ui/Button'
import { CheckCircle, XCircle, Wallet, Clock } from 'lucide-react'
import { formatDate, formatCurrency } from '@/shared/utils'
import { usePagination } from '@/shared/hooks'
import { toast } from '@/store/useToastStore'
import { getApiError } from '@/api/client'
import { usePayoutRequests, useResolvePayoutRequest } from '@/api/hooks'
import type { PayoutRequest } from '@/api/services/payoutRequestService'
import type { ColumnDef } from '@tanstack/react-table'

const PAYMENT_METHODS = ['BANK_TRANSFER', 'CASH', 'CARD', 'WALLET', 'EXTERNAL']

export function PayoutRequestsPage() {
  const { t } = useTranslation()
  const [status, setStatus] = useState('PENDING')
  const { pagination, setPagination } = usePagination()

  const params = { page: pagination.page, limit: pagination.per_page, status: status || undefined }
  const q = usePayoutRequests(params)
  const resolve = useResolvePayoutRequest()

  const rows = useMemo(() => q.data?.requests ?? [], [q.data])
  const total = q.data?.total ?? 0
  const perPage = q.data?.count_per_page ?? pagination.per_page
  const lastPage = Math.max(1, Math.ceil(total / (perPage || 1)))

  // The list is scoped to the selected status, so aggregate what's on screen.
  const pendingTotal = useMemo(
    () => rows.filter(r => r.status === 'PENDING').reduce((s, r) => s + (r.amount_requested || 0), 0),
    [rows],
  )
  const pendingCount = useMemo(() => rows.filter(r => r.status === 'PENDING').length, [rows])

  // Resolve modal state.
  const [active, setActive] = useState<PayoutRequest | null>(null)
  const [action, setAction] = useState<'approve' | 'reject'>('approve')
  const [paymentMethod, setPaymentMethod] = useState(PAYMENT_METHODS[0])
  const [txnId, setTxnId] = useState('')
  const [remarks, setRemarks] = useState('')

  const openResolve = (req: PayoutRequest, act: 'approve' | 'reject') => {
    setActive(req)
    setAction(act)
    setPaymentMethod(PAYMENT_METHODS[0])
    setTxnId('')
    setRemarks('')
  }
  const closeResolve = () => setActive(null)

  const submitResolve = () => {
    if (!active) return
    if (action === 'reject' && !remarks.trim()) {
      toast.error(t('payoutRequests.reason.required'))
      return
    }
    resolve.mutate(
      {
        request_id: active.id,
        action,
        ...(action === 'approve'
          ? { payment_method: paymentMethod, txn_id: txnId.trim() || undefined }
          : {}),
        remarks: remarks.trim() || undefined,
      },
      {
        onSuccess: () => {
          toast.success(action === 'approve' ? t('payoutRequests.toast.approved') : t('payoutRequests.toast.rejected'))
          closeResolve()
        },
        onError: (e) => toast.error(getApiError(e)),
      },
    )
  }

  const columns: ColumnDef<PayoutRequest>[] = [
    { header: t('payoutRequests.columns.id'), accessorKey: 'id',
      cell: ({ getValue }) => <span className="font-mono text-xs text-gray-400">#{getValue<number>()}</span> },
    { header: t('payoutRequests.columns.trainer'), id: 'trainer', cell: ({ row }) => (
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{row.original.trainer_name || `#${row.original.trainer_id}`}</p>
          {row.original.trainer_phone && <p className="text-xs text-gray-400">{row.original.trainer_phone}</p>}
        </div>
    )},
    { header: t('payoutRequests.columns.amount'), accessorKey: 'amount_requested',
      cell: ({ getValue }) => <span className="font-bold">{formatCurrency(getValue<number>())}</span> },
    { header: t('payoutRequests.columns.balance'), accessorKey: 'balance_at_request',
      cell: ({ getValue }) => <span className="text-sm text-gray-500">{formatCurrency(getValue<number>())}</span> },
    { header: t('payoutRequests.columns.note'), accessorKey: 'trainer_note',
      cell: ({ getValue }) => <span className="text-xs text-gray-500 line-clamp-2">{getValue<string>() || '—'}</span> },
    { header: t('payoutRequests.columns.requestedAt'), accessorKey: 'requested_at',
      cell: ({ getValue }) => <span className="text-xs text-gray-500">{getValue<string>() ? formatDate(getValue<string>()!) : '—'}</span> },
    { header: t('payoutRequests.columns.status'), accessorKey: 'status',
      cell: ({ getValue }) => <StatusBadge status={getValue<string>()} /> },
    { header: t('payoutRequests.columns.actions'), id: 'actions', cell: ({ row }) => row.original.status === 'PENDING' ? (
        <div className="flex items-center gap-2">
          <button
            onClick={() => openResolve(row.original, 'approve')}
            className="flex items-center gap-1 px-2.5 py-1 text-xs bg-green-500 text-white rounded-lg hover:bg-green-600"
          >
            <CheckCircle size={12} /> {t('payoutRequests.approve')}
          </button>
          <button
            onClick={() => openResolve(row.original, 'reject')}
            className="flex items-center gap-1 px-2.5 py-1 text-xs bg-red-500 text-white rounded-lg hover:bg-red-600"
          >
            <XCircle size={12} /> {t('payoutRequests.reject')}
          </button>
        </div>
    ) : (
        row.original.admin_remarks
          ? <span className="text-xs text-gray-400 line-clamp-1">{row.original.admin_remarks}</span>
          : <span className="text-xs text-gray-300">—</span>
    )},
  ]

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('payoutRequests.title')}
        breadcrumbs={[{ label: t('payoutRequests.breadcrumb.dashboard'), to: '/dashboard' }, { label: t('payoutRequests.breadcrumb.self') }]}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <StatCard title={t('payoutRequests.stats.pendingCount')} value={String(pendingCount)} icon={<Clock size={18} />} color="amber" />
        <StatCard title={t('payoutRequests.stats.pendingAmount')} value={formatCurrency(pendingTotal)} icon={<Wallet size={18} />} color="blue" />
      </div>

      <Card padding={false}>
        <div className="p-5 border-b border-gray-100">
          <FilterBar
            filters={[{
              type: 'select', label: t('payoutRequests.filters.status'), value: status, onChange: (v) => { setStatus(v); setPagination(p => ({ ...p, page: 1 })) },
              options: [
                { value: 'PENDING',  label: t('payoutRequests.status.pending') },
                { value: 'APPROVED', label: t('payoutRequests.status.approved') },
                { value: 'REJECTED', label: t('payoutRequests.status.rejected') },
                { value: '',         label: t('payoutRequests.status.all') },
              ],
            }]}
          />
        </div>
        {q.isError && <p className="px-5 py-4 text-sm text-red-500">{t('payoutRequests.error')}</p>}
        <DataTable<PayoutRequest>
          columns={columns}
          data={rows}
          isLoading={q.isLoading}
          emptyMessage={t('payoutRequests.empty')}
          pagination={{ page: pagination.page, per_page: perPage, total, last_page: lastPage }}
          onPaginationChange={setPagination}
        />
      </Card>

      <Modal
        open={!!active}
        onClose={closeResolve}
        title={action === 'approve' ? t('payoutRequests.modal.approveTitle') : t('payoutRequests.modal.rejectTitle')}
        size="md"
      >
        {active && (
          <div className="space-y-4">
            <div className="rounded-xl bg-gray-50 p-4 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-gray-500">{t('payoutRequests.columns.trainer')}</span><span className="font-medium">{active.trainer_name || `#${active.trainer_id}`}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">{t('payoutRequests.columns.amount')}</span><span className="font-bold">{formatCurrency(active.amount_requested)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">{t('payoutRequests.columns.balance')}</span><span>{formatCurrency(active.balance_at_request)}</span></div>
              {active.trainer_note && <div className="pt-1 text-xs text-gray-500">“{active.trainer_note}”</div>}
            </div>

            {action === 'approve' && (
              <>
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">{t('payoutRequests.modal.paymentMethod')}</span>
                  <select
                    value={paymentMethod}
                    onChange={e => setPaymentMethod(e.target.value)}
                    className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand-400"
                  >
                    {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">{t('payoutRequests.modal.txnId')}</span>
                  <input
                    value={txnId}
                    onChange={e => setTxnId(e.target.value)}
                    placeholder={t('payoutRequests.modal.txnIdPlaceholder')}
                    className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400"
                  />
                </label>
              </>
            )}

            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                {t('payoutRequests.modal.remarks')}{action === 'reject' && <span className="text-red-500"> *</span>}
              </span>
              <textarea
                value={remarks}
                onChange={e => setRemarks(e.target.value)}
                rows={3}
                placeholder={action === 'reject' ? t('payoutRequests.modal.reasonPlaceholder') : t('payoutRequests.modal.remarksPlaceholder')}
                className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400"
              />
            </label>

            <div className="flex items-center justify-end gap-3 pt-2">
              <Button variant="outline" onClick={closeResolve}>{t('common.cancel')}</Button>
              <Button
                variant={action === 'approve' ? 'primary' : 'danger'}
                loading={resolve.isPending}
                onClick={submitResolve}
              >
                {action === 'approve' ? t('payoutRequests.modal.confirmApprove') : t('payoutRequests.modal.confirmReject')}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
